import { Controller } from "@hotwired/stimulus"

function formatLapTimeMs(ms) {
  const totalSeconds = Math.floor(ms / 1000)
  const millis = ms % 1000
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  if (minutes > 0) {
    return `${minutes}:${String(seconds).padStart(2, "0")}.${String(millis).padStart(3, "0")}`
  }
  return `${seconds}.${String(millis).padStart(3, "0")}`
}

function formatDate(dateStr) {
  const d = new Date(dateStr)
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ]
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`
}

export default class extends Controller {
  static targets = ["canvas"]
  static values = { points: Array, color: String }

  _cssVar(name) {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim()
  }

  connect() {
    this.render()
    this.resizeObserver = new ResizeObserver(() => this.render())
    this.resizeObserver.observe(this.canvasTarget)
    this._onAppearanceChange = () => this.render()
    window.addEventListener("appearance:change", this._onAppearanceChange)
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    this.removeTooltip()
    window.removeEventListener("appearance:change", this._onAppearanceChange)
  }

  render() {
    const container = this.canvasTarget
    // Clear previous content safely
    while (container.firstChild) {
      container.removeChild(container.firstChild)
    }

    const points = this.pointsValue
    if (!points || points.length < 2) return

    const width = container.clientWidth
    const height = container.clientHeight
    if (width === 0 || height === 0) return

    const color = this.colorValue || "#6366f1"
    const pad = { x: 4, y: 4 }
    const dotR = 3

    const times = points.map((p) => p.time_ms)
    const minTime = Math.min(...times)
    const maxTime = Math.max(...times)

    const timeRange = maxTime - minTime || 1

    // Map to SVG coords — Y inverted so lower time = higher on screen
    // X uses equal spacing (index-based) so clustered sessions don't bunch up
    const coords = points.map((p, i) => ({
      x:
        pad.x +
        dotR +
        (i / (points.length - 1)) * (width - 2 * pad.x - 2 * dotR),
      y:
        pad.y +
        dotR +
        ((p.time_ms - minTime) / timeRange) * (height - 2 * pad.y - 2 * dotR),
      time_ms: p.time_ms,
      date: p.date,
      session_id: p.session_id,
    }))

    const ns = "http://www.w3.org/2000/svg"
    const svg = document.createElementNS(ns, "svg")
    svg.setAttribute("viewBox", `0 0 ${width} ${height}`)
    svg.setAttribute("width", "100%")
    svg.setAttribute("height", "100%")
    svg.style.overflow = "visible"

    // Polyline
    const polyline = document.createElementNS(ns, "polyline")
    polyline.setAttribute(
      "points",
      coords.map((c) => `${c.x},${c.y}`).join(" "),
    )
    polyline.setAttribute("fill", "none")
    polyline.setAttribute("stroke", color)
    polyline.setAttribute("stroke-width", "1.5")
    polyline.setAttribute("stroke-linejoin", "round")
    polyline.setAttribute("stroke-linecap", "round")
    svg.appendChild(polyline)

    // Dots
    coords.forEach((c) => {
      const circle = document.createElementNS(ns, "circle")
      circle.setAttribute("cx", c.x)
      circle.setAttribute("cy", c.y)
      circle.setAttribute("r", dotR)
      circle.setAttribute("fill", color)
      circle.style.cursor = "pointer"

      circle.addEventListener("mouseenter", (event) => {
        this.showTooltip(event, c)
      })
      circle.addEventListener("mousemove", (event) => {
        this.moveTooltip(event)
      })
      circle.addEventListener("mouseleave", () => {
        this.removeTooltip()
      })
      circle.addEventListener("click", () => {
        window.Turbo.visit(`/sessions/${c.session_id}`)
      })

      svg.appendChild(circle)
    })

    container.appendChild(svg)
  }

  showTooltip(event, point) {
    this.removeTooltip()

    const tip = document.createElement("div")
    tip.className = "dashboard-sparkline-tooltip"
    const tooltipBg = this._cssVar("--app-text")
    const tooltipFg = this._cssVar("--app-surface")
    tip.style.cssText = `position:fixed;pointer-events:none;background:${tooltipBg};color:${tooltipFg};padding:6px 10px;border-radius:6px;font-size:12px;line-height:1.4;z-index:50;white-space:nowrap;`

    const time = document.createElement("span")
    time.style.fontWeight = "600"
    time.textContent = formatLapTimeMs(point.time_ms)
    tip.appendChild(time)

    tip.appendChild(document.createElement("br"))

    const date = document.createElement("span")
    date.style.opacity = "0.7"
    date.textContent = formatDate(point.date)
    tip.appendChild(date)

    document.body.appendChild(tip)
    this._tooltip = tip
    this.moveTooltip(event)
  }

  moveTooltip(event) {
    if (!this._tooltip) return
    this._tooltip.style.left = `${event.clientX + 12}px`
    this._tooltip.style.top = `${event.clientY - 10}px`
  }

  removeTooltip() {
    if (this._tooltip) {
      this._tooltip.remove()
      this._tooltip = null
    }
  }
}
